import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '../components/ui/table';
import { ArrowLeft, Phone, PhoneIncoming, PhoneOutgoing, PhoneMissed, CheckCircle2 } from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { format } from 'date-fns';
import { fr } from 'date-fns/locale';

const API = process.env.REACT_APP_BACKEND_URL;

function formatDateTime(d) {
    if (!d) return '-';
    try { return format(new Date(d), 'dd/MM/yyyy à HH:mm', { locale: fr }); } catch { return d; }
}

function formatDuree(sec) {
    if (!sec) return '0:00';
    const m = Math.floor(sec / 60);
    const s = sec % 60;
    return `${m}:${String(s).padStart(2, '0')}`;
}

export default function AppelsPage() {
    const navigate = useNavigate();
    const [searchParams, setSearchParams] = useSearchParams();
    const [nonRappeles, setNonRappeles] = useState([]);
    const [historique, setHistorique] = useState([]);
    const [loading, setLoading] = useState(true);
    const [keyyoConnected, setKeyyoConnected] = useState(false);
    const [operatorFilter, setOperatorFilter] = useState('all'); // 'all' | 'ovh' | 'keyyo' | 'manuel'

    const fetchData = async () => {
        setLoading(true);
        try {
            const [nrRes, histRes, keyyoRes] = await Promise.all([
                axios.get(`${API}/api/calls/non-rappeles`, { withCredentials: true }),
                axios.get(`${API}/api/calls`, { withCredentials: true }),
                axios.get(`${API}/api/calls/sync/keyyo/status`, { withCredentials: true }).catch(() => ({ data: { connected: false } })),
            ]);
            setNonRappeles(nrRes.data);
            setHistorique(histRes.data);
            setKeyyoConnected(keyyoRes.data?.connected || false);
        } catch {
            // Silent — évite les toasts répétitifs si le polling échoue
        } finally { setLoading(false); }
    };

    useEffect(() => {
        // Capture ?code= from Keyyo OAuth callback
        const code = searchParams.get('code');
        if (code) {
            (async () => {
                try {
                    const redirectUri = `${window.location.origin}/appels`;
                    await axios.post(`${API}/api/calls/sync/keyyo/callback`, { code, redirect_uri: redirectUri }, { withCredentials: true });
                    toast.success('Keyyo connecté avec succès');
                    // Sync immediately
                    await axios.post(`${API}/api/calls/sync/keyyo`, {}, { withCredentials: true }).catch(() => {});
                } catch (err) {
                    toast.error('Erreur lors de la connexion Keyyo: ' + (err?.response?.data?.detail || err?.message || ''));
                }
                // Remove ?code= from URL
                setSearchParams({});
                fetchData();
            })();
        } else {
            fetchData();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const connectKeyyo = async () => {
        try {
            const origin = window.location.origin;
            const { data } = await axios.get(`${API}/api/calls/sync/keyyo/authorize-url`, {
                params: { origin },
                withCredentials: true,
            });
            window.location.href = data.url;
        } catch { toast.error('Erreur'); }
    };

    const handleMarquerRappele = async (id) => {
        if (!window.confirm('Confirmer que cet appel a bien été rappelé ?')) return;
        try {
            await axios.post(`${API}/api/calls/${id}/marquer-rappele`, {}, { withCredentials: true });
            fetchData();
        } catch {}
    };

    return (
        <div className="min-h-screen bg-slate-50 px-6 py-6">
            <div className="max-w-7xl mx-auto space-y-5">
                <div className="flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <Button variant="ghost" size="sm" onClick={() => navigate(-1)} data-testid="appels-back">
                            <ArrowLeft size={18} /><span className="ml-1">Retour</span>
                        </Button>
                        <h1 className="text-2xl font-semibold text-slate-800 flex items-center gap-2">
                            <Phone size={22} className="text-[#2e7cd6]" />
                            Appels
                        </h1>
                    </div>
                    <div className="flex items-center gap-2">
                        {!keyyoConnected && (
                            <Button
                                variant="default"
                                size="sm"
                                onClick={connectKeyyo}
                                className="bg-emerald-600 hover:bg-emerald-700 text-white"
                                data-testid="connect-keyyo-btn"
                            >
                                <Phone size={14} className="mr-1.5" />
                                Connecter Keyyo
                            </Button>
                        )}
                    </div>
                </div>

                {/* Section non rappelés */}
                <Card className="border-2 border-red-300" data-testid="appels-non-rappeles-section">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <CardTitle className="text-base font-semibold text-slate-700 flex items-center gap-2">
                            <PhoneMissed size={18} className="text-red-500" />
                            Appels non rappelés
                            <Badge variant="outline" className="bg-red-100 text-red-700 border-red-300 ml-1">{nonRappeles.length}</Badge>
                        </CardTitle>
                    </CardHeader>
                    <CardContent className="pt-4">
                        {loading ? <p className="text-sm text-slate-400 italic text-center py-3">Chargement...</p> :
                            nonRappeles.length === 0 ? (
                                <p className="text-sm text-slate-400 italic text-center py-3">Tous les appels manqués ont été rappelés ✓</p>
                            ) : (
                                <div className="space-y-2">
                                    {nonRappeles.map(c => (
                                        <div key={c.id} className="flex items-center justify-between p-3 rounded-lg bg-red-50 border border-red-200" data-testid={`non-rappele-${c.id}`}>
                                            <div className="flex items-center gap-3 flex-1 min-w-0">
                                                <PhoneMissed size={16} className="text-red-500 shrink-0" />
                                                <div className="min-w-0">
                                                    <p className="text-sm font-medium text-slate-800 truncate">
                                                        {c.prospect_nom ? <span className="text-[#2e7cd6]">{c.prospect_nom}</span> : <span className="text-slate-700">Inconnu</span>}
                                                        <span className="text-slate-500 ml-2">{c.numero}</span>
                                                    </p>
                                                    <p className="text-xs text-slate-500">
                                                        {formatDateTime(c.started_at)} · via <span className={c.operator === 'ovh' ? 'text-orange-600 font-medium' : 'text-emerald-600 font-medium'}>{c.operator}</span>
                                                        {c.ligne_appelee && <span className="text-slate-400 ml-1">→ {c.ligne_appelee}</span>}
                                                    </p>
                                                </div>
                                            </div>
                                            <Button size="sm" variant="outline" onClick={() => handleMarquerRappele(c.id)} className="shrink-0" data-testid={`marquer-rappele-${c.id}`}>
                                                <CheckCircle2 size={14} className="mr-1 text-emerald-600" /> Marquer rappelé
                                            </Button>
                                        </div>
                                    ))}
                                </div>
                            )}
                    </CardContent>
                </Card>

                {/* Section historique */}
                <Card data-testid="appels-historique-section">
                    <CardHeader className="pb-3 border-b border-slate-100">
                        <div className="flex flex-wrap items-center justify-between gap-3">
                            <CardTitle className="text-base font-semibold text-slate-700">Historique des 30 derniers jours</CardTitle>
                            <div className="flex items-center gap-1" data-testid="appels-operator-filter">
                                {[
                                    { key: 'all', label: 'Tous', count: historique.length },
                                    { key: 'ovh', label: 'OVH', count: historique.filter(c => c.operator === 'ovh').length },
                                    { key: 'keyyo', label: 'Keyyo', count: historique.filter(c => c.operator === 'keyyo').length },
                                    { key: 'manuel', label: 'Manuel', count: historique.filter(c => c.operator === 'manuel').length },
                                ].map(f => (
                                    <Button
                                        key={f.key}
                                        size="sm"
                                        variant={operatorFilter === f.key ? 'default' : 'outline'}
                                        onClick={() => setOperatorFilter(f.key)}
                                        className="h-8"
                                        data-testid={`filter-operator-${f.key}`}
                                    >
                                        {f.label} <span className="ml-1.5 text-xs opacity-70">({f.count})</span>
                                    </Button>
                                ))}
                            </div>
                        </div>
                    </CardHeader>
                    <CardContent className="pt-4">
                        {historique.length === 0 ? (
                            <p className="text-sm text-slate-400 italic text-center py-6">Aucun appel</p>
                        ) : (
                            <div className="overflow-x-auto">
                                <Table data-testid="appels-table" className="min-w-[820px]">
                                    <TableHeader>
                                        <TableRow>
                                            <TableHead className="w-12"></TableHead>
                                            <TableHead>Contact</TableHead>
                                            <TableHead>Numéro</TableHead>
                                            <TableHead>Date / Heure</TableHead>
                                            <TableHead>Durée</TableHead>
                                            <TableHead>Ligne</TableHead>
                                            <TableHead>Opérateur</TableHead>
                                            <TableHead className="text-right">Statut</TableHead>
                                        </TableRow>
                                    </TableHeader>
                                    <TableBody>
                                        {historique.filter(c => operatorFilter === 'all' || c.operator === operatorFilter).map(c => {
                                            const isIn = c.direction === 'in';
                                            const Icon = !c.answered && isIn ? PhoneMissed : isIn ? PhoneIncoming : PhoneOutgoing;
                                            const iconBg = !c.answered && isIn ? 'bg-red-50' : isIn ? 'bg-emerald-50' : 'bg-blue-50';
                                            const iconColor = !c.answered && isIn ? 'text-red-500' : isIn ? 'text-emerald-600' : 'text-blue-600';
                                            return (
                                                <TableRow key={c.id} data-testid={`hist-${c.id}`} className="hover:bg-slate-50">
                                                    <TableCell className="w-12">
                                                        <div className={`w-9 h-9 rounded-full ${iconBg} flex items-center justify-center`}>
                                                            <Icon size={16} className={iconColor} />
                                                        </div>
                                                    </TableCell>
                                                    <TableCell>
                                                        <p className="font-medium text-slate-900">
                                                            {c.prospect_nom ? <span className="text-[#2e7cd6]">{c.prospect_nom}</span> : <span className="text-slate-500 italic">Inconnu</span>}
                                                        </p>
                                                    </TableCell>
                                                    <TableCell className="text-slate-700 font-mono text-sm">
                                                        {c.numero}
                                                    </TableCell>
                                                    <TableCell className="text-slate-600 text-sm">
                                                        {formatDateTime(c.started_at)}
                                                    </TableCell>
                                                    <TableCell className="text-slate-600 text-sm tabular-nums">
                                                        {formatDuree(c.duration_seconds)}
                                                    </TableCell>
                                                    <TableCell className="text-slate-500 text-xs font-mono">
                                                        {c.ligne_appelee || '-'}
                                                    </TableCell>
                                                    <TableCell>
                                                        <Badge variant="outline" className={`text-xs ${c.operator === 'ovh' ? 'bg-orange-50 text-orange-700 border-orange-200' : c.operator === 'keyyo' ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-slate-50 text-slate-600 border-slate-200'}`}>
                                                            {c.operator}
                                                        </Badge>
                                                    </TableCell>
                                                    <TableCell className="text-right">
                                                        <div className="flex flex-col items-end gap-0.5">
                                                            <Badge variant="outline" className={`text-xs ${isIn ? (c.answered ? 'bg-emerald-50 text-emerald-700 border-emerald-200' : 'bg-red-50 text-red-700 border-red-200') : 'bg-blue-50 text-blue-700 border-blue-200'}`}>
                                                                {isIn ? (c.answered ? 'Entrant' : 'Manqué') : 'Sortant'}
                                                            </Badge>
                                                            {c.rappele_manually && c.rappele_by_name && (
                                                                <span className="text-[10px] text-emerald-700 font-medium flex items-center gap-0.5" title={`Marqué rappelé par ${c.rappele_by_name}`}>
                                                                    <CheckCircle2 size={10} /> Rappelé manuellement · {c.rappele_by_name}
                                                                </span>
                                                            )}
                                                        </div>
                                                    </TableCell>
                                                </TableRow>
                                            );
                                        })}
                                    </TableBody>
                                </Table>
                            </div>
                        )}
                    </CardContent>
                </Card>
            </div>
        </div>
    );
}
